"use client";

import React, { useState } from "react";
import { Lead } from "@/lib/db";
import { X, Send, Sparkles, Calendar, FileText, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { convertCurrency } from "@/lib/currency";

interface StageApprovalModalProps {
  lead: Lead;
  targetStage: "Proposal Sent" | "Negotiation" | "Closed Won";
  onClose: () => void;
  onConfirm: (data: {
    status: Lead["status"];
    estimated_deal_value?: number;
    currency?: "AED" | "USD" | "INR";
    options?: {
      proposalScope?: string;
      deliveryTimeline?: string;
      proposalLink?: string;
      revisedNotes?: string;
      invoiceRef?: string;
      kickoffDate?: string;
    };
  }) => void;
}

export default function StageApprovalModal({
  lead,
  targetStage,
  onClose,
  onConfirm,
}: StageApprovalModalProps) {
  // Currency state (defaults to lead's captured currency)
  const [currency, setCurrency] = useState<"AED" | "USD" | "INR">(
    (lead.currency as "AED" | "USD" | "INR") || (lead.budget_range?.includes("₹") ? "INR" : lead.budget_range?.includes("AED") ? "AED" : "USD")
  );

  // Proposal Sent fields
  const [dealValue, setDealValue] = useState<number>(
    lead.estimated_deal_value || (currency === "INR" ? 9999 : currency === "AED" ? 520 : 139)
  );
  const [proposalScope, setProposalScope] = useState<string>(
    lead.project_details || `${lead.service_type} - Complete architecture, full-stack development, and production rollout.`
  );
  const [deliveryTimeline, setDeliveryTimeline] = useState<string>(lead.timeline || "6-8 Weeks");
  const [proposalLink, setProposalLink] = useState<string>("");

  // Negotiation fields
  const [revisedNotes, setRevisedNotes] = useState<string>(
    "Recalibrated milestone payment schedule and Phase 1 deliverables to meet your target budget."
  );

  // Closed Won fields
  const [invoiceRef, setInvoiceRef] = useState<string>(
    `JS-INV-${new Date().getFullYear()}-${String(lead.id).padStart(4, "0")}`
  );
  const [kickoffDate, setKickoffDate] = useState<string>("Immediate Sprint 1 Kickoff (Next Monday)");

  const [loading, setLoading] = useState(false);

  const sym = currency === "INR" ? "₹" : currency === "AED" ? "AED " : "$";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (targetStage === "Proposal Sent") {
      onConfirm({
        status: "Proposal Sent",
        estimated_deal_value: dealValue,
        currency,
        options: {
          proposalScope,
          deliveryTimeline,
          proposalLink: proposalLink.trim() || undefined,
        },
      });
    } else if (targetStage === "Negotiation") {
      onConfirm({
        status: "Negotiation",
        estimated_deal_value: dealValue,
        currency,
        options: {
          revisedNotes,
        },
      });
    } else if (targetStage === "Closed Won") {
      onConfirm({
        status: "Closed Won",
        estimated_deal_value: dealValue,
        currency,
        options: {
          invoiceRef,
          kickoffDate,
        },
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden">
        
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-bold text-white shadow-sm ${
              targetStage === "Proposal Sent" ? "bg-amber-500" :
              targetStage === "Negotiation" ? "bg-orange-500" : "bg-emerald-500"
            }`}>
              {targetStage === "Closed Won" ? "🎉" : "⚡"}
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                {targetStage === "Proposal Sent" && `Dispatch Proposal to ${lead.name}`}
                {targetStage === "Negotiation" && `Send Revised Quote to ${lead.name}`}
                {targetStage === "Closed Won" && `Confirm Payment & Kickoff Pack`}
              </h3>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                {lead.service_type} • {lead.company_name || lead.email}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Form Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          
          {/* Dual Currency Selector */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80">
            <div>
              <span className="text-xs font-bold text-slate-900 dark:text-white block">Commercial Currency</span>
              <span className="text-[11px] text-slate-500 dark:text-slate-400">Select currency for client proposal & invoice</span>
            </div>
            <div className="inline-flex p-1 rounded-lg bg-slate-200/80 dark:bg-slate-900 border border-slate-300/60 dark:border-slate-700/60 text-xs font-bold shadow-inner">
              <button
                type="button"
                onClick={() => {
                  if (currency !== "AED" && dealValue > 0) {
                    setDealValue(convertCurrency(dealValue, currency, "AED"));
                  }
                  setCurrency("AED");
                }}
                className={`px-2.5 py-1 rounded-md transition-all flex items-center gap-1 cursor-pointer ${
                  currency === "AED"
                    ? "bg-white dark:bg-slate-800 text-primary shadow-xs"
                    : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                }`}
              >
                <span>🇦🇪</span>
                <span>AED (د.إ)</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (currency !== "USD" && dealValue > 0) {
                    setDealValue(convertCurrency(dealValue, currency, "USD"));
                  }
                  setCurrency("USD");
                }}
                className={`px-2.5 py-1 rounded-md transition-all flex items-center gap-1 cursor-pointer ${
                  currency === "USD"
                    ? "bg-white dark:bg-slate-800 text-primary shadow-xs"
                    : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                }`}
              >
                <span>🇺🇸</span>
                <span>USD ($)</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (currency !== "INR" && dealValue > 0) {
                    setDealValue(convertCurrency(dealValue, currency, "INR"));
                  }
                  setCurrency("INR");
                }}
                className={`px-2.5 py-1 rounded-md transition-all flex items-center gap-1 cursor-pointer ${
                  currency === "INR"
                    ? "bg-white dark:bg-slate-800 text-primary shadow-xs"
                    : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                }`}
              >
                <span>🇮🇳</span>
                <span>INR (₹)</span>
              </button>
            </div>
          </div>

          {/* ======================= PROPOSAL SENT FORM ======================= */}
          {targetStage === "Proposal Sent" && (
            <>
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-900 dark:text-amber-300 text-xs flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <span>
                  Review and customize the engineering proposal before it is automatically emailed to <strong>{lead.email}</strong> in <strong>{currency} ({sym})</strong>.
                </span>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Estimated Investment / Deal Value ({sym})
                </label>
                <div className="relative">
                  <span className="w-4 h-4 absolute left-3 top-2.5 text-sm font-bold text-slate-400">
                    {sym}
                  </span>
                  <input
                    type="number"
                    min="0"
                    step={currency === "INR" ? "5000" : "500"}
                    required
                    value={dealValue}
                    onChange={(e) => setDealValue(Number(e.target.value))}
                    className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Project Scope & Deliverables
                </label>
                <textarea
                  rows={3}
                  required
                  value={proposalScope}
                  onChange={(e) => setProposalScope(e.target.value)}
                  placeholder="Summarize key features, architecture, and deliverables..."
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs leading-relaxed text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Delivery Timeline
                  </label>
                  <input
                    type="text"
                    required
                    value={deliveryTimeline}
                    onChange={(e) => setDeliveryTimeline(e.target.value)}
                    placeholder="e.g. 6-8 Weeks"
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-medium text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Proposal URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={proposalLink}
                    onChange={(e) => setProposalLink(e.target.value)}
                    placeholder="https://drive.google.com/..."
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>
            </>
          )}

          {/* ======================= NEGOTIATION FORM ======================= */}
          {targetStage === "Negotiation" && (
            <>
              <div className="p-3 rounded-xl bg-orange-500/10 border border-orange-500/20 text-orange-900 dark:text-orange-300 text-xs flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                <span>
                  Adjust your quotation or milestone pacing in <strong>{currency} ({sym})</strong> to close the negotiation with <strong>{lead.name}</strong>.
                </span>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Revised Deal Value / Final Quotation ({sym})
                </label>
                <div className="relative">
                  <span className="w-4 h-4 absolute left-3 top-2.5 text-sm font-bold text-slate-400">
                    {sym}
                  </span>
                  <input
                    type="number"
                    min="0"
                    step={currency === "INR" ? "5000" : "500"}
                    required
                    value={dealValue}
                    onChange={(e) => setDealValue(Number(e.target.value))}
                    className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Adjustment & Realignment Notes for Client
                </label>
                <textarea
                  rows={3}
                  required
                  value={revisedNotes}
                  onChange={(e) => setRevisedNotes(e.target.value)}
                  placeholder="Explain the changes in scope, milestone payments, or delivery schedule..."
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs leading-relaxed text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </>
          )}

          {/* ======================= CLOSED WON FORM ======================= */}
          {targetStage === "Closed Won" && (
            <>
              <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-900 dark:text-emerald-300 text-xs flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                <span>
                  Confirm payment receipt in <strong>{currency} ({sym})</strong>. The client will automatically receive the official invoice reference and Sprint 1 kickoff package!
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Invoice Reference #
                  </label>
                  <input
                    type="text"
                    required
                    value={invoiceRef}
                    onChange={(e) => setInvoiceRef(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-mono font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Confirmed Deal Value ({sym})
                  </label>
                  <input
                    type="number"
                    min="0"
                    required
                    value={dealValue}
                    onChange={(e) => setDealValue(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-bold text-emerald-600 dark:text-emerald-400 focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Project Kickoff Date
                </label>
                <input
                  type="text"
                  required
                  value={kickoffDate}
                  onChange={(e) => setKickoffDate(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-medium text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="pt-2 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-2.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              className="text-xs"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={loading}
              className={`text-xs font-bold flex items-center gap-1.5 ${
                targetStage === "Proposal Sent" ? "bg-amber-600 hover:bg-amber-700 text-white" :
                targetStage === "Negotiation" ? "bg-orange-600 hover:bg-orange-700 text-white" :
                "bg-emerald-600 hover:bg-emerald-700 text-white"
              }`}
            >
              <Send className="w-3.5 h-3.5" />
              {loading ? "Sending..." : targetStage === "Proposal Sent" ? "Approve & Send Proposal" : targetStage === "Negotiation" ? "Send Revised Quotation" : "Confirm Payment & Send Kickoff"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
